/**
 * ROBOTIS Parametric Filter — Shopify storefront
 * Replaces BigCommerce GraphQL + jQuery implementation.
 */
(function () {
  'use strict';

  var FILTER_CACHE_PREFIX = 'robotis-filter:filters:';
  var FILTER_CACHE_TTL = 15 * 60 * 1000;
  var CARD_FETCH_CONCURRENCY = 6;

  var sharedCatalog = {
    map: null,
    byHandle: null,
    promise: null
  };

  var sharedCardHtmlCache = Object.create(null);

  function readSessionCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.expires < Date.now()) {
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    } catch (e) {
      return null;
    }
  }

  function writeSessionCache(key, value) {
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({ expires: Date.now() + FILTER_CACHE_TTL, value: value })
      );
    } catch (e) {
      /* storage full or unavailable */
    }
  }

  function mapWithConcurrency(items, limit, worker) {
    var results = new Array(items.length);
    var nextIndex = 0;

    function runWorker() {
      var current = nextIndex;
      if (current >= items.length) return Promise.resolve();
      nextIndex += 1;
      return Promise.resolve(worker(items[current], current))
        .then(function (result) {
          results[current] = result;
          return runWorker();
        })
        .catch(function (err) {
          results[current] = null;
          return runWorker();
        });
    }

    var runners = [];
    var pool = Math.max(1, Math.min(limit, items.length));
    for (var i = 0; i < pool; i++) {
      runners.push(runWorker());
    }
    return Promise.all(runners).then(function () {
      return results.filter(function (item) {
        return item != null && item !== '';
      });
    });
  }

  function indexCatalogProducts(products, map, byHandle) {
    (products || []).forEach(function (product) {
      if (!product) return;
      if (product.id != null) {
        map[String(product.id)] = product;
      }
      if (product.handle) {
        byHandle[product.handle] = product;
      }
    });
  }

  var INFO_DESCRIPTIONS = {
    'Input Voltage': 'The rated input voltage level for driving the DYNAMIXEL actuator.',
    'No Load RPM': 'The maximum rotational speed of the actuator with nothing attached to the output horn.',
    'Stall Torque': 'The maximum amount of torque the actuator can produce in stall conditions. For maximum lifetime, the actuator should spend no more than 20% of its operating time producing this output torque.',
    'Stall Current': 'The maximum current draw of the actuator in a stall condition, which represents the maximum current the actuator will draw during operation.',
    'Weight': 'The total weight of the actuator.',
    'Communications Protocol': 'The different physical communication protocols supported by the actuator.',
    'Size': 'Size and series - dimensions can be found on the individual product pages.',
    'Operating Modes': 'The different DYNAMIXEL operating modes supported by the actuator.',
    'Special Features': 'Additional hardware or environmental capabilities supported by the actuator.'
  };

  // Exact 9 filters + display order matching live BigCommerce / design image
  var FILTER_DISPLAY_ORDER = [
    'Stall Torque',
    'No Load RPM',
    'Input Voltage',
    'Stall Current',
    'Communications Protocol',
    'Size',
    'Operating Modes',
    'Weight',
    'Special Features'
  ];

  // Display titles with units (match design image)
  var FILTER_TITLE_UNITS = {
    'Stall Torque': 'Nm',
    'No Load RPM': 'RPM',
    'Input Voltage': 'v',
    'Stall Current': 'A'
  };

  // Preferred value order for text filters (design / live site)
  var FILTER_VALUE_ORDER = {
    'Communications Protocol': ['TTL', 'RS485', 'ModBUS', 'SBUS', 'iBUS', 'RC PWM'],
    'Operating Modes': [
      'Position Control Mode',
      'Velocity Control Mode',
      'Extended Position Control Mode',
      'PWM Control Mode',
      'Current Control Mode',
      'Current Based Position Control Mode'
    ],
    'Special Features': [
      'Dual Joint Port',
      'External Input Port',
      'External Import Port',
      'Dual Axis',
      'Waterproof (IP68)',
      'RC Protocols'
    ]
  };

  // API still needs default min/max ranges for these when not list-selected
  var RANGE_FIELDS = ['Stall Torque', 'No Load RPM', 'Stall Current'];

  var PARTIAL_MATCH_KEYS = [
    'operating_modes',
    'communications_protocol',
    'special_features'
  ];

  function parseFilterNumber(value) {
    var match = String(value || '').match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : NaN;
  }

  function sortFilterValues(name, values) {
    var list = (values || []).slice();
    if (!list.length) return list;

    var preferred = FILTER_VALUE_ORDER[name];
    if (preferred && preferred.length) {
      return list.sort(function (a, b) {
        var ia = preferred.indexOf(a);
        var ib = preferred.indexOf(b);
        if (ia === -1) ia = 9999;
        if (ib === -1) ib = 9999;
        if (ia !== ib) return ia - ib;
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    // Numeric filters + Size: natural / numeric ascending
    if (
      name === 'Stall Torque' ||
      name === 'No Load RPM' ||
      name === 'Stall Current' ||
      name === 'Input Voltage' ||
      name === 'Weight' ||
      name === 'Size'
    ) {
      return list.sort(function (a, b) {
        var na = parseFilterNumber(a);
        var nb = parseFilterNumber(b);
        var aNum = !isNaN(na);
        var bNum = !isNaN(nb);
        if (aNum && bNum && na !== nb) return na - nb;
        if (aNum && !bNum) return -1;
        if (!aNum && bNum) return 1;
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    return list.sort(function (a, b) {
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function slugifyParam(name) {
    return String(name || '')
      .replace(/\s+/g, '_')
      .toLowerCase();
  }

  function formatMoney(cents, moneyFormat) {
    if (cents == null || isNaN(cents)) return '';
    var amount = (Number(cents) / 100).toFixed(2);
    var format = moneyFormat || '${{amount}}';
    if (format.indexOf('{{amount_no_decimals}}') !== -1) {
      return format.replace('{{amount_no_decimals}}', String(Math.round(Number(cents) / 100)));
    }
    if (format.indexOf('{{amount_with_comma_separator}}') !== -1) {
      return format.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    }
    return format.replace('{{amount}}', amount).replace('{{amount_with_currency}}', amount);
  }

  function buildFormBody(data) {
    var params = new URLSearchParams();

    function append(key, value) {
      if (value == null) return;
      if (Array.isArray(value)) {
        value.forEach(function (item) {
          append(key + '[]', item);
        });
        return;
      }
      if (typeof value === 'object') {
        Object.keys(value).forEach(function (childKey) {
          append(key + '[' + childKey + ']', value[childKey]);
        });
        return;
      }
      params.append(key, String(value));
    }

    Object.keys(data || {}).forEach(function (key) {
      append(key, data[key]);
    });

    return params;
  }

  function joinUrl(base, path) {
    var b = String(base || '').replace(/\/?$/, '/');
    var p = String(path || '').replace(/^\//, '');
    return b + p;
  }

  function infoIconSvg() {
    return (
      '<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<g opacity="0.5">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M7.5 0C11.6393 0 15 3.3607 15 7.5C15 11.6393 11.6393 15 7.5 15C3.3607 15 0 11.6393 0 7.5C0 3.3607 3.3607 0 7.5 0ZM7.5 1.04651C3.93837 1.04651 1.04651 3.93837 1.04651 7.5C1.04651 11.0616 3.93837 13.9535 7.5 13.9535C11.0616 13.9535 13.9535 11.0616 13.9535 7.5C13.9535 3.93837 11.0616 1.04651 7.5 1.04651Z" fill="black"/>' +
      '<path d="M7.5 11.4244C7.88532 11.4244 8.19768 11.1121 8.19768 10.7267C8.19768 10.3414 7.88532 10.0291 7.5 10.0291C7.11469 10.0291 6.80233 10.3414 6.80233 10.7267C6.80233 11.1121 7.11469 11.4244 7.5 11.4244Z" fill="black"/>' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M6.62791 4.44767L6.97674 8.63372C6.97674 8.92256 7.21116 9.15698 7.5 9.15698C7.78884 9.15698 8.02326 8.92256 8.02326 8.63372L8.37209 4.44767C8.37209 4.15884 8.19767 3.57558 7.5 3.57558C6.80233 3.57558 6.62791 4.15884 6.62791 4.44767Z" fill="black"/>' +
      '</g></svg>'
    );
  }

  function RobotisFilter(root) {
    this.root = root;
    this.config = this.readConfig();
    this.productQueue = [];
    this.pageIndex = 0;
    this.pageSize = this.config.productsPerPage || 12;
    this.isLoadingProducts = false;
    this.disableTimer = null;
    this.defaultRanges = {};
    this.productIdMap = null;
    this.productHandleMap = null;
    this.productIdMapPromise = null;
    this.availabilityAbort = null;
    this.availabilityRequestId = 0;

    this.els = {
      panel: qs(root, '.robotis-filter__panel'),
      filtersLoading: qs(root, '[data-rf-filters-loading]'),
      filtersError: qs(root, '[data-rf-filters-error]'),
      cardsWrap: qs(root, '.robotis-filter__cards-wrap'),
      cards: qs(root, '[data-rf-cards]'),
      selectedList: qs(root, '[data-rf-selected-list]'),
      resetAll: qs(root, '[data-rf-reset-all]'),
      apply: qs(root, '[data-rf-apply]'),
      results: qs(root, '[data-rf-results]'),
      resultCount: qs(root, '[data-rf-result-count]'),
      resultsLoading: qs(root, '[data-rf-results-loading]'),
      empty: qs(root, '[data-rf-empty]'),
      gridContainer: qs(root, '.robotis-filter__grid-container'),
      grid: qs(root, '[data-rf-grid]'),
      loadMore: qs(root, '[data-rf-load-more]'),
      pages: qs(root, '[data-rf-pages]')
    };

    this.bindStaticEvents();
    this.init();
  }

  RobotisFilter.prototype.readConfig = function () {
    var node = qs(this.root, '[data-robotis-filter-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.error('[RobotisFilter] Invalid config JSON', e);
      return {};
    }
  };

  RobotisFilter.prototype.bindStaticEvents = function () {
    var self = this;

    if (this.els.resetAll) {
      this.els.resetAll.addEventListener('click', function () {
        self.resetAll();
      });
    }

    if (this.els.apply) {
      this.els.apply.addEventListener('click', function () {
        self.applyFilters();
      });
    }

    if (this.els.loadMore) {
      this.els.loadMore.addEventListener('click', function () {
        self.loadNextPage(true);
      });
    }

    this.root.addEventListener('click', function (event) {
      var atcBtn = event.target.closest('[data-rf-add-to-cart]');
      if (atcBtn && self.root.contains(atcBtn)) {
        event.preventDefault();
        self.handleAddToCart(atcBtn);
        return;
      }

      var valueEl = event.target.closest('.filter-data p.rated-torque');
      if (valueEl && self.root.contains(valueEl) && !valueEl.classList.contains('filter_unavailable')) {
        self.toggleValue(valueEl);
        return;
      }

      var resetBtn = event.target.closest('.reset-select');
      if (resetBtn && self.root.contains(resetBtn)) {
        self.resetCard(resetBtn.closest('.custom-filtcard'));
      }
    });

    // Capture add-to-cart forms injected with Liquid cards (prevents /cart page redirect).
    this.root.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || form.tagName !== 'FORM' || !self.root.contains(form)) return;

        var action = form.getAttribute('action') || '';
        var isCartForm =
          form.getAttribute('data-type') === 'add-to-cart-form' ||
          action.indexOf('/cart/add') !== -1;

        if (!isCartForm) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        self.handleAddToCartFromForm(form, event.submitter);
      },
      true
    );
  };

  RobotisFilter.prototype.init = function () {
    var self = this;
    this.setFiltersLoading(true);
    // Warm the Shopify catalog map while filters load (speeds up Apply).
    this.ensureProductIdMap();

    this.fetchFilters()
      .then(function (data) {
        self.renderFilters(data || []);
        self.setFiltersLoading(false);
        self.scheduleAvailabilityUpdate();
      })
      .catch(function (err) {
        console.error('[RobotisFilter] Failed to load filters', err);
        self.setFiltersLoading(false);
        self.showFiltersError('Unable to load filters. Please try again later.');
      });
  };

  RobotisFilter.prototype.setFiltersLoading = function (isLoading) {
    if (this.els.filtersLoading) {
      this.els.filtersLoading.hidden = !isLoading;
    }
    if (this.els.cardsWrap) {
      this.els.cardsWrap.hidden = isLoading;
    }
    if (this.els.panel) {
      this.els.panel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.els.panel.classList.toggle('is-loading-filters', isLoading);
    }
  };

  RobotisFilter.prototype.setApplyBusy = function (isBusy) {
    if (!this.els.apply) return;
    this.els.apply.disabled = !!isBusy;
    this.els.apply.classList.toggle('is-loading', !!isBusy);
    this.els.apply.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  };

  RobotisFilter.prototype.setAvailabilityBusy = function (isBusy) {
    if (this.els.panel) {
      this.els.panel.classList.toggle('is-updating-availability', !!isBusy);
    }
  };

  RobotisFilter.prototype.showFiltersError = function (message) {
    if (!this.els.filtersError) return;
    this.els.filtersError.hidden = false;
    this.els.filtersError.textContent = message;
  };

  RobotisFilter.prototype.postApi = function (endpoint, payload, options) {
    var url = joinUrl(this.config.apiBase, endpoint);
    var fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json, text/javascript, */*; q=0.01'
      },
      body: buildFormBody(payload)
    };
    if (options && options.signal) {
      fetchOptions.signal = options.signal;
    }
    return fetch(url, fetchOptions).then(function (res) {
      if (!res.ok) throw new Error('API ' + res.status + ' for ' + endpoint);
      return res.json();
    });
  };

  RobotisFilter.prototype.fetchFilters = function () {
    var cacheKey =
      FILTER_CACHE_PREFIX +
      String(this.config.apiBase || '') +
      ':' +
      String(this.config.filtersEndpoint || 'new_ajax_file.php');

    var cached = readSessionCache(cacheKey);
    if (cached) {
      return Promise.resolve(cached);
    }

    return this.postApi(this.config.filtersEndpoint || 'new_ajax_file.php', {
      token: this.config.filtersToken || 'iijdifdofiiuter'
    }).then(function (data) {
      writeSessionCache(cacheKey, data);
      return data;
    });
  };

  RobotisFilter.prototype.renderFilters = function (items) {
    var self = this;
    this.defaultRanges = {};
    if (this.els.cards) this.els.cards.innerHTML = '';

    var byName = {};
    (items || []).forEach(function (item) {
      if (!item || !item.customfield_name) return;
      byName[item.customfield_name] = item;
    });

    // Always render in design order (9 filters). Skip Rated Torque / extras.
    var rendered = 0;
    var cardsHtml = [];

    FILTER_DISPLAY_ORDER.forEach(function (name, index) {
      var item = byName[name];
      if (!item) return;

      var values = sortFilterValues(name, item.customfield_values || []);

      if (RANGE_FIELDS.indexOf(name) !== -1) {
        self.defaultRanges[name] = self.getDefaultRange(item.customfield_values || []);
      }

      cardsHtml.push(self.buildListCardHtml(name, values, index));
      rendered += 1;
    });

    if (this.els.cards) {
      this.els.cards.innerHTML = cardsHtml.join('');
      this.els.cards.classList.toggle('rf-cards--nine', rendered === 9);
    }
  };

  RobotisFilter.prototype.getDefaultRange = function (values) {
    var nums = (values || [])
      .map(function (val) {
        return parseFilterNumber(val);
      })
      .filter(function (n) {
        return !isNaN(n);
      });

    if (!nums.length) {
      return ['0', '0'];
    }

    // Previous site used noUiSlider with places:0 → integer min/max strings
    var max = Math.ceil(Math.max.apply(null, nums));
    return ['0', String(max)];
  };

  RobotisFilter.prototype.buildTitleHtml = function (name) {
    var info = INFO_DESCRIPTIONS[name] || '';
    var unit = FILTER_TITLE_UNITS[name] || '';
    var label = unit ? name + ' (' + unit + ')' : name;

    var infoHtml = '';
    if (info) {
      infoHtml =
        '<div class="info_details-container">' +
        '<p class="info-detail_icon">' +
        infoIconSvg() +
        '</p>' +
        '<p class="information_details">' +
        escapeHtml(info) +
        '</p>' +
        '</div>';
    }

    return (
      '<div class="custom-filttitle" data-parameter-name="' +
      escapeHtml(name) +
      '" parameter_name="' +
      escapeHtml(name) +
      '">' +
      infoHtml +
      '<div class="title_name">' +
      escapeHtml(label) +
      '</div></div>'
    );
  };

  RobotisFilter.prototype.valuesHtml = function (values) {
    if (!values || !values.length) {
      return '<p class="rated-torque" style="pointer-events:none;">No available values.</p>';
    }
    return values
      .map(function (value) {
        return '<p class="rated-torque">' + escapeHtml(value) + '</p>';
      })
      .join('');
  };

  RobotisFilter.prototype.buildListCardHtml = function (name, values, index) {
    return (
      '<div class="custom-filtcard datacards-' +
      index +
      '" data-filter-name="' +
      escapeHtml(name) +
      '">' +
      this.buildTitleHtml(name) +
      '<div class="cust-filterserch"><div class="filter-data">' +
      this.valuesHtml(values) +
      '</div></div>' +
      '<p class="reset-select" hidden>Reset</p>' +
      '</div>'
    );
  };

  RobotisFilter.prototype.renderListCard = function (name, values, index) {
    if (this.els.cards) {
      this.els.cards.insertAdjacentHTML('beforeend', this.buildListCardHtml(name, values, index));
    }
  };

  RobotisFilter.prototype.toggleValue = function (el) {
    el.classList.toggle('active');
    var card = el.closest('.custom-filtcard');
    this.syncCardReset(card);
    this.updateSelectedDisplay();
    this.scheduleAvailabilityUpdate();
  };

  RobotisFilter.prototype.syncCardReset = function (card) {
    if (!card) return;
    var resetBtn = qs(card, '.reset-select');
    var hasActive = qsa(card, '.filter-data p.active').length > 0;
    if (resetBtn) resetBtn.hidden = !hasActive;
  };

  RobotisFilter.prototype.resetCard = function (card) {
    if (!card) return;
    qsa(card, '.filter-data p.active').forEach(function (item) {
      item.classList.remove('active');
    });
    this.syncCardReset(card);
    this.updateSelectedDisplay();
    this.scheduleAvailabilityUpdate();
  };

  RobotisFilter.prototype.resetAll = function () {
    var self = this;
    qsa(this.root, '.custom-filtcard').forEach(function (card) {
      qsa(card, '.filter-data p.active').forEach(function (item) {
        item.classList.remove('active');
      });
      self.syncCardReset(card);
    });

    this.updateSelectedDisplay();
    this.scheduleAvailabilityUpdate();
  };

  RobotisFilter.prototype.updateSelectedDisplay = function () {
    var selected = qsa(this.root, '.filter-data p.active').map(function (item) {
      return item.textContent.trim();
    });
    if (this.els.selectedList) {
      this.els.selectedList.textContent = selected.join(', ');
    }
  };

  RobotisFilter.prototype.collectSelectedFilters = function () {
    var self = this;
    var selected = {};

    qsa(this.root, '.filter-data p.active').forEach(function (el) {
      var card = el.closest('.custom-filtcard');
      var title = card ? qs(card, '.custom-filttitle') : null;
      var name = title && (title.getAttribute('parameter_name') || title.dataset.parameterName);
      if (!name) return;
      if (!selected[name]) {
        selected[name] = { values: [], type: 'normal' };
      }
      selected[name].values.push(el.textContent.trim());
    });

    // Match previous site payload:
    // selected_filters[Stall Torque][values][]=0.215Nm
    // selected_filters[Stall Torque][type]=normal
    // selected_filters[No Load RPM][]=0
    // selected_filters[No Load RPM][]=5675
    // selected_filters[Stall Current][]=0
    // selected_filters[Stall Current][]=22
    RANGE_FIELDS.forEach(function (name) {
      if (selected[name]) return;
      if (self.defaultRanges[name]) {
        selected[name] = self.defaultRanges[name].slice();
      }
    });

    return selected;
  };

  RobotisFilter.prototype.scheduleAvailabilityUpdate = function () {
    var self = this;
    clearTimeout(this.disableTimer);
    this.disableTimer = setTimeout(function () {
      self.updateAvailability(self.collectSelectedFilters());
    }, 250);
  };

  RobotisFilter.prototype.hasListSelection = function (combined) {
    if (!combined) return false;
    return Object.keys(combined).some(function (key) {
      var val = combined[key];
      return val && typeof val === 'object' && !Array.isArray(val) && val.type === 'normal';
    });
  };

  RobotisFilter.prototype.updateAvailability = function (combined) {
    var self = this;

    if (this.availabilityAbort) {
      this.availabilityAbort.abort();
    }
    this.availabilityAbort = new AbortController();
    var signal = this.availabilityAbort.signal;
    var requestId = ++this.availabilityRequestId;

    // Only disable options after a real list selection (same as previous site click).
    // Default range arrays alone are not a user selection.
    if (!this.hasListSelection(combined)) {
      qsa(this.root, '.filter-data p.rated-torque').forEach(function (el) {
        el.classList.remove('filter_unavailable');
      });
      this.setAvailabilityBusy(false);
      return;
    }

    this.setAvailabilityBusy(true);

    qsa(this.root, '.filter-data p.rated-torque').forEach(function (el) {
      if (el.style.pointerEvents === 'none') return;
      el.classList.add('filter_unavailable');
    });

    this.postApi(
      this.config.availableEndpoint || 'disable_unavailable_options.php',
      {
        token: this.config.resultsToken || 'iijdifdofiiuterab',
        selected_filters: combined
      },
      { signal: signal }
    )
      .then(function (data) {
        if (requestId !== self.availabilityRequestId) return;

        var backend = (data && data.available_filters) || {};
        var backendKeys = Object.keys(backend);

        // If API returns no availability map, fail open.
        if (!backendKeys.length) {
          qsa(self.root, '.filter-data p.filter_unavailable').forEach(function (el) {
            el.classList.remove('filter_unavailable');
          });
          return;
        }

        qsa(self.root, '.custom-filtcard .filter-data').forEach(function (filterData) {
          var title = qs(filterData.closest('.custom-filtcard'), '.custom-filttitle');
          if (!title) return;
          var filterName = slugifyParam(
            title.getAttribute('parameter_name') || title.dataset.parameterName || ''
          );
          var backendValues = backend[filterName];
          if (!backendValues) return;

          qsa(filterData, 'p.rated-torque').forEach(function (frontendVal) {
            var text = frontendVal.textContent.trim();
            var match = false;

            backendValues.forEach(function (value) {
              var v = String(value).trim();
              if (PARTIAL_MATCH_KEYS.indexOf(filterName) !== -1) {
                if (v.indexOf(text) !== -1) match = true;
              } else if (text === v) {
                match = true;
              }
            });

            if (match) frontendVal.classList.remove('filter_unavailable');
          });
        });

        // Keep active selections visible
        qsa(self.root, '.filter-data p.active').forEach(function (el) {
          el.classList.remove('filter_unavailable');
        });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.error('[RobotisFilter] Availability error', err);
        // Fail open: allow all values
        qsa(self.root, '.filter-data p.filter_unavailable').forEach(function (el) {
          el.classList.remove('filter_unavailable');
        });
      })
      .finally(function () {
        if (requestId === self.availabilityRequestId) {
          self.setAvailabilityBusy(false);
        }
      });
  };

  RobotisFilter.prototype.applyFilters = function () {
    var self = this;
    var combined = this.collectSelectedFilters();

    this.productQueue = [];
    this.pageIndex = 0;
    if (this.els.grid) this.els.grid.innerHTML = '';
    if (this.els.resultCount) this.els.resultCount.textContent = '';
    if (this.els.pages) this.els.pages.innerHTML = '';
    if (this.els.loadMore) this.els.loadMore.hidden = true;
    if (this.els.empty) this.els.empty.hidden = true;
    if (this.els.results) this.els.results.hidden = false;
    this.setApplyBusy(true);
    this.setResultsLoading(true);

    // Same API constraint: need at least one list selection.
    // Ranges alone are defaults merged for API compatibility.
    if (!this.hasListSelection(combined)) {
      this.setApplyBusy(false);
      this.setResultsLoading(false);
      if (this.els.resultCount) this.els.resultCount.textContent = '';
      if (this.els.empty) {
        this.els.empty.hidden = false;
        this.els.empty.textContent = 'Please select at least one filter before applying.';
      }
      return;
    }

    this.postApi(this.config.resultsEndpoint || 'filter_results.php', {
      token: this.config.resultsToken || 'iijdifdofiiuterab',
      selected_filters: combined
    })
      .then(function (data) {
        self.setApplyBusy(false);
        var items = self.normalizeProductRefs(data);
        if (self.els.resultCount) {
          self.els.resultCount.textContent = items.length ? '(' + items.length + ')' : '(0)';
        }

        if (!items.length) {
          self.setResultsLoading(false);
          if (self.els.empty) {
            self.els.empty.hidden = false;
            self.els.empty.textContent =
              self.config.emptyText || 'No products match your selected filters.';
          }
          return;
        }

        self.productQueue = self.chunk(items, self.pageSize);
        self.pageIndex = 0;

        if (self.config.enableLoadMore) {
          self.loadNextPage(false);
        } else {
          self.renderPagination();
          self.loadPage(0, false);
        }
      })
      .catch(function (err) {
        console.error('[RobotisFilter] Apply error', err);
        self.setApplyBusy(false);
        self.setResultsLoading(false);
        if (self.els.empty) {
          self.els.empty.hidden = false;
          self.els.empty.textContent = 'Unable to load filtered products. Please try again.';
        }
      });
  };

  RobotisFilter.prototype.normalizeProductRefs = function (data) {
    if (!data) return [];

    if (Array.isArray(data.products)) {
      return data.products.map(function (p) {
        if (typeof p === 'string' || typeof p === 'number') {
          return { handle: String(p), id: p };
        }
        return {
          handle: p.handle || p.product_handle || p.url_handle || null,
          id: p.id || p.product_id || p.shopify_id || null,
          product: p
        };
      });
    }

    if (Array.isArray(data.product_handles)) {
      return data.product_handles.map(function (handle) {
        return { handle: String(handle), id: null };
      });
    }

    if (Array.isArray(data.product_ids)) {
      return data.product_ids.map(function (id) {
        var str = String(id);
        // Shopify handles are typically non-numeric; numeric may still be handles in some migrations
        var looksLikeHandle = /[a-zA-Z-]/.test(str);
        return {
          handle: looksLikeHandle ? str : null,
          id: id,
          raw: id
        };
      });
    }

    return [];
  };

  RobotisFilter.prototype.chunk = function (arr, size) {
    var groups = [];
    for (var i = 0; i < arr.length; i += size) {
      groups.push(arr.slice(i, i + size));
    }
    return groups;
  };

  RobotisFilter.prototype.setResultsLoading = function (isLoading) {
    this.isLoadingProducts = isLoading;
    if (this.els.resultsLoading) this.els.resultsLoading.hidden = !isLoading;
    if (this.els.gridContainer) {
      this.els.gridContainer.classList.toggle('is-loading', isLoading);
      this.els.gridContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    if (this.els.results) {
      this.els.results.classList.toggle('is-loading-results', isLoading);
    }
  };

  RobotisFilter.prototype.renderPagination = function () {
    var self = this;
    if (!this.els.pages) return;
    this.els.pages.innerHTML = '';
    if (this.productQueue.length <= 1) return;

    var prev = document.createElement('li');
    prev.className = 'result_page_changer changer_disabled';
    prev.textContent = 'Prev';
    prev.addEventListener('click', function () {
      self.goToPage(self.pageIndex - 1);
    });
    this.els.pages.appendChild(prev);

    this.productQueue.forEach(function (_, index) {
      var li = document.createElement('li');
      li.textContent = String(index + 1);
      li.dataset.page = String(index);
      if (index === 0) li.classList.add('current__result_page');
      li.addEventListener('click', function () {
        self.goToPage(index);
      });
      self.els.pages.appendChild(li);
    });

    var next = document.createElement('li');
    next.className = 'result_page_changer';
    next.textContent = 'Next';
    next.addEventListener('click', function () {
      self.goToPage(self.pageIndex + 1);
    });
    this.els.pages.appendChild(next);
  };

  RobotisFilter.prototype.goToPage = function (index) {
    if (index < 0 || index >= this.productQueue.length) return;
    this.loadPage(index, false);
  };

  RobotisFilter.prototype.loadNextPage = function (append) {
    if (this.pageIndex >= this.productQueue.length) {
      if (this.els.loadMore) this.els.loadMore.hidden = true;
      return;
    }
    this.loadPage(this.pageIndex, append);
  };

  RobotisFilter.prototype.loadPage = function (index, append) {
    var self = this;
    var group = this.productQueue[index];
    if (!group) return;

    this.setResultsLoading(true);
    if (!append && this.els.grid) this.els.grid.innerHTML = '';
    if (this.els.empty) this.els.empty.hidden = true;

    var renderedCount = 0;

    this.fetchThemeProductCards(group, function (html) {
      if (!html || !self.els.grid) return;
      self.els.grid.insertAdjacentHTML('beforeend', html);
      renderedCount += 1;
      if (renderedCount === 1) {
        self.setResultsLoading(false);
      }
    })
      .then(function (htmlItems) {
        if (!renderedCount && !htmlItems.length && !append) {
          if (self.els.empty) {
            self.els.empty.hidden = false;
            self.els.empty.textContent =
              self.config.emptyText || 'No products match your selected filters.';
          }
        }

        self.pageIndex = index + 1;

        if (self.config.enableLoadMore) {
          if (self.els.loadMore) {
            self.els.loadMore.hidden = self.pageIndex >= self.productQueue.length;
          }
        } else if (self.els.pages) {
          qsa(self.els.pages, 'li').forEach(function (li) {
            li.classList.remove('current__result_page', 'changer_disabled');
          });
          var current = qs(self.els.pages, 'li[data-page="' + index + '"]');
          if (current) current.classList.add('current__result_page');
          var changers = qsa(self.els.pages, '.result_page_changer');
          if (changers[0] && index <= 0) changers[0].classList.add('changer_disabled');
          if (changers[1] && index >= self.productQueue.length - 1) {
            changers[1].classList.add('changer_disabled');
          }
        }
      })
      .catch(function (err) {
        console.error('[RobotisFilter] Product load error', err);
        if (self.els.empty) {
          self.els.empty.hidden = false;
          self.els.empty.textContent = 'Unable to load product details.';
        }
      })
      .finally(function () {
        self.setResultsLoading(false);
      });
  };

  RobotisFilter.prototype.getRoutesRoot = function () {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return this.config.routesRoot || '/';
  };

  RobotisFilter.prototype.ensureProductIdMap = function () {
    var self = this;

    if (sharedCatalog.map) {
      this.productIdMap = sharedCatalog.map;
      this.productHandleMap = sharedCatalog.byHandle;
      return Promise.resolve(sharedCatalog.map);
    }

    if (this.productIdMap) {
      return Promise.resolve(this.productIdMap);
    }

    if (sharedCatalog.promise) {
      return sharedCatalog.promise.then(function (map) {
        self.productIdMap = map;
        self.productHandleMap = sharedCatalog.byHandle;
        return map;
      });
    }

    if (this.productIdMapPromise) {
      return this.productIdMapPromise;
    }

    sharedCatalog.promise = (function loadPages(page, map, byHandle) {
      var root = self.getRoutesRoot();
      return fetch(root + 'products.json?limit=250&page=' + page, {
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('products.json ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];
          indexCatalogProducts(products, map, byHandle);
          if (products.length < 250) {
            sharedCatalog.map = map;
            sharedCatalog.byHandle = byHandle;
            self.productIdMap = map;
            self.productHandleMap = byHandle;
            return map;
          }
          return loadPages(page + 1, map, byHandle);
        });
    })(1, {}, {});

    this.productIdMapPromise = sharedCatalog.promise;

    return sharedCatalog.promise.catch(function (err) {
      console.error('[RobotisFilter] Failed to build product ID map', err);
      sharedCatalog.promise = null;
      self.productIdMapPromise = null;
      self.productIdMap = {};
      self.productHandleMap = {};
      sharedCatalog.map = self.productIdMap;
      sharedCatalog.byHandle = self.productHandleMap;
      return self.productIdMap;
    });
  };

  RobotisFilter.prototype.resolveCatalogProducts = function (refs) {
    var self = this;
    return this.ensureProductIdMap().then(function (map) {
      var found = [];
      var missing = [];

      refs.forEach(function (ref) {
        var product = null;

        if (ref.id != null && map[String(ref.id)]) {
          product = map[String(ref.id)];
        } else if (ref.handle) {
          product = self.findProductByHandle(map, ref.handle);
        } else if (ref.id != null && /[a-zA-Z-]/.test(String(ref.id))) {
          product = self.findProductByHandle(map, String(ref.id));
        }

        if (product) {
          found.push(product);
        } else {
          missing.push(ref.id || ref.handle);
        }
      });

      if (missing.length) {
        console.warn(
          '[RobotisFilter] Product IDs not found in this store catalog:',
          missing
        );
      }

      return found;
    });
  };

  RobotisFilter.prototype.findProductByHandle = function (map, handle) {
    if (this.productHandleMap && this.productHandleMap[handle]) {
      return this.productHandleMap[handle];
    }
    if (sharedCatalog.byHandle && sharedCatalog.byHandle[handle]) {
      return sharedCatalog.byHandle[handle];
    }
    var keys = Object.keys(map || {});
    for (var i = 0; i < keys.length; i++) {
      if (map[keys[i]] && map[keys[i]].handle === handle) {
        return map[keys[i]];
      }
    }
    return null;
  };

  RobotisFilter.prototype.fetchThemeProductCards = function (refs, onCard) {
    var self = this;

    return this.resolveCatalogProducts(refs).then(function (products) {
      if (!products.length) return [];

      var slots = new Array(products.length);
      var completed = new Array(products.length);
      var nextRender = 0;

      function flushReadyCards() {
        if (typeof onCard !== 'function') return;
        while (nextRender < products.length && completed[nextRender]) {
          if (slots[nextRender]) onCard(slots[nextRender]);
          nextRender += 1;
        }
      }

      return mapWithConcurrency(products, CARD_FETCH_CONCURRENCY, function (product, index) {
        return self
          .fetchCardProductHtml(product)
          .then(function (html) {
            var card = self.isUsableCardHtml(html)
              ? html
              : self.themeCardHtmlFromCatalog(product);
            slots[index] = card || null;
            completed[index] = true;
            flushReadyCards();
            return card;
          })
          .catch(function () {
            slots[index] = null;
            completed[index] = true;
            flushReadyCards();
            return null;
          });
      });
    });
  };

  RobotisFilter.prototype.isUsableCardHtml = function (html) {
    if (!html) return false;
    var temp = document.createElement('div');
    temp.innerHTML = html;
    // Reject full page / section chrome dumps
    if (temp.querySelector('header, footer, #MainContent, .shopify-section-header')) return false;
    var item = temp.querySelector('li.grid__item, .card-wrapper, .rf-product-card');
    if (!item) return false;
    // Reject near-empty ratio shells that only contain a tiny image node
    var text = (item.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > 2 || !!item.querySelector('img');
  };

  RobotisFilter.prototype.fetchCardProductHtml = function (product) {
    if (!product || !product.handle) return Promise.resolve(null);

    var handle = product.handle;
    if (sharedCardHtmlCache[handle]) {
      return Promise.resolve(sharedCardHtmlCache[handle]);
    }

    var root = this.getRoutesRoot();
    var view = this.config.cardView || 'robotis-filter-card';
    var url = root + 'products/' + encodeURIComponent(handle) + '?view=' + encodeURIComponent(view);

    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (html) {
        if (!html || !String(html).trim()) return null;

        var temp = document.createElement('div');
        temp.innerHTML = html;
        var item = temp.querySelector('li.grid__item');
        if (item) {
          sharedCardHtmlCache[handle] = item.outerHTML;
          return sharedCardHtmlCache[handle];
        }

        var card = temp.querySelector('.card-wrapper product-card-wrapper, .card-wrapper, .product-card-wrapper');
        if (card) {
          sharedCardHtmlCache[handle] = (
            '<li class="grid__item" data-product-id="' +
            escapeHtml(product.id) +
            '" data-product-handle="' +
            escapeHtml(handle) +
            '">' +
            card.outerHTML +
            '</li>'
          );
          return sharedCardHtmlCache[handle];
        }

        return null;
      })
      .catch(function () {
        return null;
      });
  };

  RobotisFilter.prototype.themeCardHtmlFromCatalog = function (product) {
    if (!product) return '';

    var cfg = this.config;
    var root = this.getRoutesRoot();
    var handle = product.handle || '';
    var url = product.url || root + 'products/' + encodeURIComponent(handle);
    var title = product.title || 'Product';
    var vendor = product.vendor || '';
    var images = product.images || [];
    var image =
      (images[0] && (images[0].src || images[0])) || null;
    var image2 =
      (images[1] && (images[1].src || images[1])) || null;
    var variant = (product.variants && product.variants[0]) || {};
    var variantId = variant.id || '';
    var available = variant.available !== false && product.available !== false;
    var sku = variant.sku || '';
    var price = variant.price != null ? variant.price : product.price;
    var compare =
      variant.compare_at_price != null ? variant.compare_at_price : product.compare_at_price;

    var priceText = this.formatCatalogPrice(price);
    var compareText = '';
    if (compare && String(compare) !== String(price) && Number(compare) > Number(price)) {
      compareText = this.formatCatalogPrice(compare);
    }

    var ratio = cfg.imageRatio || 'square';
    var shape = cfg.imageShape || 'default';
    var mediaClass =
      'rf-product-card__media rf-product-card__media--' +
      escapeHtml(ratio) +
      (shape !== 'default' ? ' rf-product-card__media--shape-' + escapeHtml(shape) : '');

    var badgeHtml = '';
    if (cfg.showBadge) {
      if (!available) {
        badgeHtml = '<span class="rf-product-card__badge rf-product-card__badge--soldout">' +
          escapeHtml(cfg.soldOutText || 'Sold out') +
          '</span>';
      } else if (compareText) {
        badgeHtml = '<span class="rf-product-card__badge">Sale</span>';
      }
    }

    var primaryImg = image
      ? '<img class="rf-product-card__img rf-product-card__img--primary" src="' +
        escapeHtml(image) +
        '" alt="' +
        escapeHtml(title) +
        '" loading="lazy" width="600" height="600">'
      : '<div class="rf-product-card__placeholder" aria-hidden="true"></div>';

    var secondaryImg =
      cfg.showSecondaryImage && image2
        ? '<img class="rf-product-card__img rf-product-card__img--secondary" src="' +
          escapeHtml(image2) +
          '" alt="" loading="lazy" width="600" height="600">'
        : '';

    var vendorHtml =
      cfg.showVendor && vendor
        ? '<p class="rf-product-card__vendor">' + escapeHtml(vendor) + '</p>'
        : '';

    var skuHtml =
      cfg.showSku && sku
        ? '<p class="rf-product-card__sku">' + escapeHtml(sku) + '</p>'
        : '';

    var ratingHtml = '';
    if (cfg.showRating) {
      var ratingValue =
        (product.rating && product.rating.value) ||
        product.average_rating ||
        (product.metafields && product.metafields.reviews && product.metafields.reviews.rating) ||
        null;
      var ratingCount =
        (product.rating && product.rating.count) ||
        product.rating_count ||
        null;
      if (ratingValue != null && !isNaN(Number(ratingValue))) {
        ratingHtml = this.ratingStarsHtml(Number(ratingValue), ratingCount);
      }
    }

    var priceHtml = '';
    if (cfg.showPrice !== false && priceText) {
      priceHtml =
        '<div class="rf-product-card__price">' +
        '<span class="rf-product-card__price-current">' +
        escapeHtml(priceText) +
        '</span>' +
        (compareText
          ? '<s class="rf-product-card__price-compare">' + escapeHtml(compareText) + '</s>'
          : '') +
        '</div>';
    }

    var actionsHtml = '';
    if (cfg.showAddToCart !== false || cfg.quickAdd === 'standard' || cfg.quickAdd === 'bulk') {
      if (!available) {
        actionsHtml =
          '<button type="button" class="rf-product-card__atc rf-product-card__atc--disabled" disabled>' +
          escapeHtml(cfg.soldOutText || 'Sold out') +
          '</button>';
      } else if (variantId) {
        actionsHtml =
          '<button type="button" class="rf-product-card__atc" data-rf-add-to-cart data-variant-id="' +
          escapeHtml(variantId) +
          '">' +
          escapeHtml(cfg.addToCartText || 'Add to cart') +
          '</button>';
      }
    }

    return (
      '<li class="grid__item" data-product-id="' +
      escapeHtml(product.id) +
      '" data-product-handle="' +
      escapeHtml(handle) +
      '">' +
      '<article class="rf-product-card">' +
      '<a class="' +
      mediaClass +
      '" href="' +
      escapeHtml(url) +
      '" aria-label="' +
      escapeHtml(title) +
      '">' +
      badgeHtml +
      primaryImg +
      secondaryImg +
      '</a>' +
      '<div class="rf-product-card__info">' +
      vendorHtml +
      skuHtml +
      '<h3 class="rf-product-card__title">' +
      '<a href="' +
      escapeHtml(url) +
      '">' +
      escapeHtml(title) +
      '</a></h3>' +
      ratingHtml +
      priceHtml +
      actionsHtml +
      '</div>' +
      '</article>' +
      '</li>'
    );
  };

  RobotisFilter.prototype.formatCatalogPrice = function (price) {
    if (price == null || price === '') return '';
    if (typeof price === 'string') {
      return price.indexOf('$') === 0 ? price : '$' + price;
    }
    if (typeof price === 'number') {
      if (price > 999) return formatMoney(price, this.config.moneyFormat);
      return formatMoney(Math.round(price * 100), this.config.moneyFormat);
    }
    return '';
  };

  RobotisFilter.prototype.ratingStarsHtml = function (rating, count) {
    var value = Math.max(0, Math.min(5, Number(rating) || 0));
    var full = Math.floor(value);
    var stars = '';
    for (var i = 1; i <= 5; i++) {
      stars +=
        '<span class="rf-product-card__star' +
        (i <= full ? ' is-full' : '') +
        '" aria-hidden="true">★</span>';
    }
    var countHtml =
      count != null
        ? '<span class="rf-product-card__rating-count">(' + escapeHtml(count) + ')</span>'
        : '';
    return (
      '<div class="rf-product-card__rating" aria-label="' +
      escapeHtml(value.toFixed(1)) +
      ' out of 5">' +
      stars +
      countHtml +
      '</div>'
    );
  };

  function getAddToCartLabel(button) {
    if (!button) return null;
    return (
      button.querySelector(
        'span:not(.sold-out-message):not(.icon-wrap):not(.loading__spinner):not(.hidden)'
      ) || button
    );
  }

  function setAddToCartLoading(button, isLoading, text) {
    if (!button) return;
    var label = getAddToCartLabel(button);
    if (!button.dataset.rfAtcOriginal && label) {
      button.dataset.rfAtcOriginal = label.textContent.trim();
    }
    if (label && text) label.textContent = text;
    button.disabled = !!isLoading;
    button.classList.toggle('is-loading', !!isLoading);
    button.classList.toggle('loading', !!isLoading);
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  function resetAddToCartButton(button) {
    if (!button) return;
    var label = getAddToCartLabel(button);
    var original = button.dataset.rfAtcOriginal;
    if (label && original) label.textContent = original;
    button.disabled = false;
    button.classList.remove('is-loading', 'loading');
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  function getCartComponent() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function extractSectionInnerHTML(html, selector) {
    if (!html) return '';
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector || '.shopify-section');
    return node ? node.innerHTML : '';
  }

  // Dawn mounts the cart count on #cart-icon-bubble in the header. The /cart/add
  // sections payload wraps that fragment in .shopify-section — never replace a
  // page-level .shopify-section or the whole header gets wiped.
  function updateCartIconBubble(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    var sectionHtml = parsedState.sections['cart-icon-bubble'];
    if (!sectionHtml) return;

    var mount = document.getElementById('cart-icon-bubble');
    if (!mount) return;

    var extractSelector = '.shopify-section';
    var cart = getCartComponent();
    var innerHtml =
      cart && typeof cart.getSectionInnerHTML === 'function'
        ? cart.getSectionInnerHTML(sectionHtml, extractSelector)
        : extractSectionInnerHTML(sectionHtml, extractSelector);

    if (!innerHtml) return;

    mount.innerHTML = innerHtml;
  }

  function updateCartSections(parsedState) {
    updateCartIconBubble(parsedState);

    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-empty');
      var drawerInner = drawer.querySelector('.drawer__inner.is-empty');
      if (drawerInner) drawerInner.classList.remove('is-empty');
    }
  }

  function notifyCartUpdate(parsedState, variantId) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'robotis-filter',
      productVariantId: String(variantId || ''),
      cartData: parsedState
    });
  }

  function fallbackRefreshCartIcon(routesRoot) {
    var root = routesRoot || '/';
    return fetch(root + 'cart.js?sections=cart-icon-bubble', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart refresh failed');
        return res.json();
      })
      .then(function (data) {
        updateCartSections(data);
        notifyCartUpdate(data);
        return data;
      });
  }

  window.robotisFilterCartUpdate = function (parsedState, variantId) {
    updateCartSections(parsedState);
    notifyCartUpdate(parsedState, variantId);
  };

  RobotisFilter.prototype.getCartComponent = function () {
    return getCartComponent();
  };

  RobotisFilter.prototype.extractSectionInnerHTML = function (html, selector) {
    return extractSectionInnerHTML(html, selector);
  };

  RobotisFilter.prototype.updateCartSections = function (parsedState) {
    updateCartSections(parsedState);
  };

  RobotisFilter.prototype.notifyCartUpdate = function (parsedState, variantId) {
    notifyCartUpdate(parsedState, variantId);
  };

  RobotisFilter.prototype.fallbackRefreshCartIcon = function () {
    return fallbackRefreshCartIcon(this.getRoutesRoot());
  };

  RobotisFilter.prototype.handleAddToCartFromForm = function (form, submitter) {
    var variantInput = form.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value || variantInput.disabled) return;

    var button =
      submitter ||
      form.querySelector('[type="submit"][name="add"]') ||
      form.querySelector('[type="submit"]');
    if (!button || button.disabled) return;

    button.setAttribute('data-variant-id', variantInput.value);
    this.handleAddToCart(button);
  };

  RobotisFilter.prototype.handleAddToCart = function (button) {
    var self = this;
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    setAddToCartLoading(button, true, 'Adding…');

    var root = this.getRoutesRoot();
    var cartAddUrl =
      (typeof routes !== 'undefined' && routes.cart_add_url) || root + 'cart/add';
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    formData.append('sections', 'cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    var cart = this.getCartComponent();
    if (cart && typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(button);
    }

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript'
      },
      body: formData
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || response.message || 'Add to cart failed');
        }

        if (response.sections) {
          self.updateCartSections(response);
          self.notifyCartUpdate(response, variantId);
        } else {
          return self.fallbackRefreshCartIcon();
        }
      })
      .then(function () {
        setAddToCartLoading(button, true, 'Added');
        setTimeout(function () {
          resetAddToCartButton(button);
        }, 1200);
      })
      .catch(function (err) {
        console.error('[RobotisFilter] Add to cart error', err);
        setAddToCartLoading(button, true, 'Error');
        setTimeout(function () {
          resetAddToCartButton(button);
        }, 1500);
      });
  };

  RobotisFilter.prototype.renderCardHtml = function (htmlItems, append) {
    if (!this.els.grid) return;
    if (!append) this.els.grid.innerHTML = '';
    if (!htmlItems.length) return;

    this.els.grid.insertAdjacentHTML('beforeend', htmlItems.join(''));
  };

  RobotisFilter.prototype.fetchProducts = function (refs) {
    return this.resolveCatalogProducts(refs).then(function (products) {
      return products;
    });
  };

  RobotisFilter.prototype.fetchShopifyProductByHandle = function (handle) {
    var self = this;
    return this.ensureProductIdMap().then(function (map) {
      var product = self.findProductByHandle(map, handle);
      return product ? self.normalizeProductObject(product) : null;
    });
  };

  RobotisFilter.prototype.fetchShopifyProductById = function (id) {
    var self = this;
    return this.ensureProductIdMap().then(function (map) {
      var product = map[String(id)];
      return product ? self.normalizeProductObject(product) : null;
    });
  };

  RobotisFilter.prototype.normalizeProductObject = function (product) {
    if (!product) return null;

    var price =
      product.price != null
        ? product.price
        : product.price_min != null
          ? product.price_min
          : product.prices && product.prices.price
            ? Math.round(Number(product.prices.price.value) * 100)
            : null;

    var compare =
      product.compare_at_price != null
        ? product.compare_at_price
        : product.compare_at_price_min != null
          ? product.compare_at_price_min
          : null;

    var image =
      product.featured_image ||
      (product.images && product.images[0]) ||
      (product.media && product.media[0] && product.media[0].preview_image && product.media[0].preview_image.src) ||
      (product.defaultImage && product.defaultImage.url) ||
      null;

    if (image && typeof image === 'object') {
      image = image.src || image.url || null;
    }

    var handle = product.handle || product.url_handle || null;
    var url = product.url || (handle ? '/products/' + handle : product.path || '#');

    return {
      id: product.id || product.entityId || null,
      handle: handle,
      title: product.title || product.name || 'Product',
      url: url,
      featured_image: image,
      vendor: product.vendor || (product.brand && product.brand.name) || '',
      price: price,
      compare_at_price: compare,
      available: product.available !== false,
      sku: product.sku || (product.variants && product.variants[0] && product.variants[0].sku) || '',
      placeholder: !!product.placeholder
    };
  };

  RobotisFilter.prototype.renderProducts = function (products, append) {
    var self = this;
    if (!this.els.grid) return;
    if (!append) this.els.grid.innerHTML = '';

    if (!products.length && !append) {
      if (this.els.empty) this.els.empty.hidden = false;
      return;
    }

    var html = products
      .map(function (product) {
        return self.productCardHtml(product);
      })
      .join('');

    this.els.grid.insertAdjacentHTML('beforeend', html);
  };

  RobotisFilter.prototype.productCardHtml = function (product) {
    var cfg = this.config;
    var image = product.featured_image
      ? '<img src="' +
        escapeHtml(product.featured_image) +
        '" alt="' +
        escapeHtml(product.title) +
        '" loading="lazy" width="600" height="600">'
      : '<div class="robotis-card__image-placeholder" aria-hidden="true"></div>';

    var badge = '';
    if (
      cfg.showBadge &&
      product.compare_at_price &&
      product.price != null &&
      Number(product.compare_at_price) > Number(product.price)
    ) {
      badge = '<span class="robotis-card__badge">Sale</span>';
    }
    if (!product.available) {
      badge = '<span class="robotis-card__badge robotis-card__badge--soldout">Sold out</span>';
    }

    var vendor =
      cfg.showVendor && product.vendor
        ? '<div class="robotis-card__vendor">' + escapeHtml(product.vendor) + '</div>'
        : '';

    var sku =
      cfg.showSku && product.sku
        ? '<div class="robotis-card__sku">' + escapeHtml(product.sku) + '</div>'
        : '';

    var priceHtml = '';
    if (cfg.showPrice && product.price != null && !product.placeholder) {
      var priceText = formatMoney(product.price, cfg.moneyFormat);
      var compareHtml = '';
      if (product.compare_at_price && Number(product.compare_at_price) > Number(product.price)) {
        compareHtml =
          '<s class="robotis-card__compare">' +
          formatMoney(product.compare_at_price, cfg.moneyFormat) +
          '</s>';
      }
      priceHtml =
        '<div class="robotis-card__price">' +
        '<span class="robotis-card__price-current">' +
        priceText +
        '</span>' +
        compareHtml +
        '</div>';
    }

    var animClass = cfg.enableAnimations ? ' robotis-card--animate' : '';

    return (
      '<li class="grid__item"><article class="robotis-card' +
      animClass +
      (product.placeholder ? ' robotis-card--placeholder' : '') +
      '">' +
      '<a class="robotis-card__media" href="' +
      escapeHtml(product.url) +
      '">' +
      badge +
      image +
      '</a>' +
      '<div class="robotis-card__body">' +
      vendor +
      sku +
      '<h3 class="robotis-card__title"><a href="' +
      escapeHtml(product.url) +
      '">' +
      escapeHtml(product.title) +
      '</a></h3>' +
      priceHtml +
      '<a class="robotis-card__cta" href="' +
      escapeHtml(product.url) +
      '">View product</a>' +
      '</div></article></li>'
    );
  };

  function boot() {
    qsa(document, '.robotis-filter').forEach(function (root) {
      if (root.dataset.rfReady) return;
      root.dataset.rfReady = '1';
      new RobotisFilter(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector
      ? event.target.querySelector('.robotis-filter')
      : null;
    if (root) {
      delete root.dataset.rfReady;
      new RobotisFilter(root);
    }
  });
})();
